// src/components/celebrate/CoinCelebration.js
//
// "You earned 5 coins!" — when coins are earned (the sbl:coins-earned event),
// a popup appears in the middle of the screen, big coins fly into a piggy
// bank, the piggy wiggles, and the new total counts up. It closes by itself
// (or with a tap). Only ONE of these listens, however many coin counters are
// on the page.
//
// Skipped for gifts and albums — those screens already show the coins.
//
// Coins that arrive from somewhere else — a teacher's Give coins, a parent's
// coin pack, a teacher's daily visit or bought pack going into the class
// coin bank — have no button press on THIS screen to announce them. So the
// host also watches the wallet's ledger and pops up for each new one, with
// the amount and what it was for. Popups queue: nothing replaces another.
//
// TEACHER GIFTS AND CHEERS are different (TeacherMessages below): that popup
// STAYS until the student presses "Thank you!" or "OK". Several waiting at
// once (three gifts sent while they were away) come as ONE popup with the
// total and each note. "Seen" is saved on the account (users/{uid}.
// teacherMsgsSeenAt), so a gift sent while they were logged out greets them
// at their next login — on any device — until they press the button.

import ModalPortal from "../common/ModalPortal";
import React, { useEffect, useRef, useState } from "react";
import { collection, doc, limit, onSnapshot, orderBy, query, setDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../../firebase/firebaseConfig";
import "./celebrate.css";

let hosts = 0; // the first mounted instance does the work

const SKIP = new Set(["chest", "album", "gift-seen"]);
const WHY = {
  game: "for playing the game",
  "first-video": "for watching the video",
  "first-music": "for listening to the song",
  "first-singalong": "for singing along",
  treasure: "from the treasure chest",
  daily: "from the daily trail",
  gift: "from your teacher",
  "educator-daily": "for visiting today — they're in your class coin bank, ready to hand out with Give coins",
  "class-bank": "from the coin pack you bought — they're in your class coin bank, ready to hand out with Give coins",
  purchase: "added to your account — have fun in the shop!",
  "educator-daily-me": "for visiting today — these are yours, for your own avatar and the shop",
};

// coins that land in a teacher's class coin bank, not their own wallet
export const BANK_REASONS = new Set(["educator-daily", "class-bank"]);

// ledger reasons nobody on this screen announced (the rest — games, videos,
// chests — are announced by the page that paid them, the moment they're paid)
// (teacher gifts have their own popup — TeacherMessages)
const FROM_ELSEWHERE = new Set(["educator-daily", "educator-daily-me", "class-bank", "purchase"]);
const TEACHER_REASONS = new Set(["gift", "cheer"]);
const TWO_WEEKS = 14 * 24 * 3600 * 1000;

const ms = (at) => (at && typeof at.toMillis === "function" ? at.toMillis() : 0);

/** Pops up for coins added to my wallet / class bank from elsewhere. */
const useLedgerArrivals = (enabled) => {
  useEffect(() => {
    if (!enabled) return undefined;
    let stopLedger = () => {};
    const stopAuth = onAuthStateChanged(getAuth(), (user) => {
      stopLedger();
      if (!user) return;
      const key = `sbl-coins-seen-${user.uid}`;
      let seen = 0;
      try {
        seen = Number(localStorage.getItem(key)) || 0;
      } catch {
        /* private mode */
      }
      // first time on this device: anything from the last 10 minutes counts
      // (a coin pack just bought, today's visit coins), older ones don't
      if (!seen) seen = Date.now() - 10 * 60 * 1000;
      stopLedger = onSnapshot(
        query(collection(db, "wallets", user.uid, "ledger"), orderBy("at", "desc"), limit(10)),
        (snap) => {
          const fresh = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((e) => ms(e.at) > seen)
            .sort((a, b) => ms(a.at) - ms(b.at));
          if (!fresh.length) return;
          seen = ms(fresh[fresh.length - 1].at);
          try {
            localStorage.setItem(key, String(seen));
          } catch {
            /* ignore */
          }
          fresh
            .filter((e) => Number(e.amount) > 0 && FROM_ELSEWHERE.has(e.reason))
            .forEach((e) => {
              const bank = BANK_REASONS.has(e.reason);
              window.dispatchEvent(
                new CustomEvent("sbl:coins-earned", {
                  detail: {
                    amount: Number(e.amount),
                    coins: Number(bank ? e.classBank ?? e.balance : e.balance) || 0,
                    reason: e.reason,
                    note: e.reason === "gift" ? String(e.label || "") : "",
                    bank,
                  },
                })
              );
            });
        },
        () => {} // rules not deployed / offline — the counters still update
      );
    });
    return () => {
      stopLedger();
      stopAuth();
    };
  }, [enabled]);
};

const Piggy = ({ wiggle }) => (
  <svg viewBox="0 0 120 100" className={`sbl-piggy ${wiggle ? "is-wiggle" : ""}`} aria-hidden="true">
    <ellipse cx="60" cy="58" rx="42" ry="32" fill="#ff9cc2" stroke="#c2477a" strokeWidth="3" />
    <ellipse cx="44" cy="44" rx="14" ry="8" fill="#ffc2da" opacity="0.8" />
    <path d="M36 30 L30 14 L46 26 Z" fill="#ff86b4" stroke="#c2477a" strokeWidth="3" strokeLinejoin="round" />
    <path d="M70 28 L78 12 L82 30 Z" fill="#ff86b4" stroke="#c2477a" strokeWidth="3" strokeLinejoin="round" />
    <rect x="48" y="24" width="24" height="5" rx="2.5" fill="#7a2650" />
    <ellipse cx="98" cy="58" rx="10" ry="9" fill="#ff86b4" stroke="#c2477a" strokeWidth="3" />
    <circle cx="95" cy="57" r="2" fill="#7a2650" />
    <circle cx="101" cy="57" r="2" fill="#7a2650" />
    <circle cx="80" cy="46" r="3.4" fill="#2a2130" />
    <circle cx="81" cy="45" r="1" fill="#fff" />
    <path d="M18 56 q-10 -4 -8 6 q2 6 -6 6" stroke="#c2477a" strokeWidth="3" fill="none" strokeLinecap="round" />
    <rect x="34" y="84" width="10" height="12" rx="4" fill="#ff86b4" stroke="#c2477a" strokeWidth="3" />
    <rect x="74" y="84" width="10" height="12" rx="4" fill="#ff86b4" stroke="#c2477a" strokeWidth="3" />
  </svg>
);

/** Teacher gifts + cheers not yet thanked for: [{ id, amount, reason, label, at }] */
const useTeacherMessages = (enabled) => {
  const [list, setList] = useState([]);
  const [uid, setUid] = useState("");
  const seenRef = useRef(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let stops = [];
    const stopAuth = onAuthStateChanged(getAuth(), (user) => {
      stops.forEach((f) => f());
      stops = [];
      setList([]);
      if (!user) return;
      setUid(user.uid);
      const lsKey = `sbl-teacher-msgs-${user.uid}`;
      let local = 0;
      try {
        local = Number(localStorage.getItem(lsKey)) || 0;
      } catch {
        /* private mode */
      }
      let fromAccount = 0;
      let rows = [];
      const recompute = () => {
        seenRef.current = Math.max(local, fromAccount, Date.now() - TWO_WEEKS);
        setList(rows.filter((e) => ms(e.at) > seenRef.current).sort((a, b) => ms(a.at) - ms(b.at)));
      };
      stops.push(
        onSnapshot(
          doc(db, "users", user.uid),
          (snap) => {
            fromAccount = Number(snap.exists() && snap.data().teacherMsgsSeenAt) || 0;
            recompute();
          },
          () => {}
        )
      );
      stops.push(
        onSnapshot(
          query(collection(db, "wallets", user.uid, "ledger"), orderBy("at", "desc"), limit(30)),
          (snap) => {
            rows = snap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .filter((e) => TEACHER_REASONS.has(e.reason) && (e.reason === "cheer" || Number(e.amount) > 0) && ms(e.at));
            recompute();
          },
          () => {}
        )
      );
    });
    return () => {
      stops.forEach((f) => f());
      stopAuth();
    };
  }, [enabled]);

  const acknowledge = () => {
    const last = list.length ? ms(list[list.length - 1].at) : 0;
    if (!last || !uid) return;
    seenRef.current = last;
    try {
      localStorage.setItem(`sbl-teacher-msgs-${uid}`, String(last));
    } catch {
      /* ignore */
    }
    setDoc(doc(db, "users", uid), { teacherMsgsSeenAt: last }, { merge: true }).catch(() => {});
    const coins = list.reduce((n, e) => n + (Number(e.amount) || 0), 0);
    const balance = Math.max(0, ...list.map((e) => Number(e.balance) || 0));
    setList([]);
    // the counters tick up now (no second piggy popup — "gift-seen" is skipped)
    if (coins > 0) {
      try {
        window.dispatchEvent(new CustomEvent("sbl:coins-earned", { detail: { amount: coins, coins: balance, reason: "gift-seen", from: { instant: true } } }));
      } catch {
        /* ignore */
      }
    }
  };
  return { list, acknowledge };
};

const splitLabel = (label = "") => {
  const i = String(label).lastIndexOf(" — ");
  return i > 0 ? { note: label.slice(0, i), who: label.slice(i + 3) } : { note: String(label), who: "" };
};

const TeacherMessages = ({ list, onDone }) => {
  const [wiggle, setWiggle] = useState(0);
  const gifts = list.filter((e) => e.reason === "gift");
  const cheers = list.filter((e) => e.reason === "cheer");
  const coins = gifts.reduce((n, e) => n + (Number(e.amount) || 0), 0);
  const who = splitLabel((gifts[0] || cheers[0] || {}).label).who || (cheers[0] || {}).from || "Your teacher";
  const balance = Math.max(0, ...gifts.map((e) => Number(e.balance) || 0));
  useEffect(() => {
    const t = setInterval(() => setWiggle((w) => w + 1), 1600);
    return () => clearInterval(t);
  }, []);
  const title = coins
    ? gifts.length > 1
      ? `${who} sent you ${gifts.length} gifts — ${coins} coins in all!`
      : `${who} sent you ${coins} coin${coins === 1 ? "" : "s"}!`
    : `${who} sent you a message!`;
  return (
    <ModalPortal>
      <div className="sbl-cc-pop is-teacher" role="dialog" aria-modal="true" aria-label="A gift from your teacher">
        <div className="sbl-cc-card is-teacher">
          <div className="sbl-cc-ribbon">🎁 From your teacher</div>
          <h2>{title}</h2>
          {coins > 0 && (
            <span className="sbl-cc-piggy" key={wiggle}>
              <Piggy wiggle={wiggle > 0} />
            </span>
          )}
          <ul className="sbl-cc-list">
            {list.map((e) => {
              const { note } = splitLabel(e.label);
              return (
                <li key={e.id} className={e.reason === "cheer" ? "is-cheer" : ""}>
                  {e.reason === "cheer" ? (
                    <>
                      <span className="sbl-cc-li-icon">📣</span> “{note || "Great job!"}”
                    </>
                  ) : (
                    <>
                      <span className="sbl-cc-li-coins">+{e.amount}</span> {note ? `“${note}”` : "coins"}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
          {coins > 0 && balance > 0 && (
            <p className="sbl-cc-total">
              Your bank: <b>{balance.toLocaleString()}</b> coins
            </p>
          )}
          <div className="sbl-cc-btns">
            <button type="button" className="sbl-cc-btn is-thanks" onClick={onDone} autoFocus>
              Thank you! 💖
            </button>
            <button type="button" className="sbl-cc-btn" onClick={onDone}>
              OK
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

const reduced = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};

const CoinCelebration = () => {
  const [isHost] = useState(() => {
    hosts += 1;
    return hosts === 1;
  });
  const [show, setShow] = useState(null); // { amount, coins, reason, key }
  const [wiggle, setWiggle] = useState(0);
  const [total, setTotal] = useState(0);
  const piggyRef = useRef(null);
  const timer = useRef(null);
  const queue = useRef([]);
  const showing = useRef(false);

  useLedgerArrivals(isHost);
  const teacherMsgs = useTeacherMessages(isHost);

  const close = () => {
    clearTimeout(timer.current);
    const next = queue.current.shift();
    if (next) {
      setShow({ ...next, key: Date.now() });
      setTotal(Math.max(0, (Number(next.coins) || 0) - next.amount));
    } else {
      showing.current = false;
      setShow(null);
    }
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(
    () => () => {
      hosts = Math.max(0, hosts - 1);
    },
    []
  );

  useEffect(() => {
    if (!isHost) return undefined;
    const on = (e) => {
      const d = e.detail || {};
      if (!(d.amount > 0) || SKIP.has(d.reason)) return;
      if (showing.current) {
        queue.current.push(d); // wait for the one on screen to finish
        return;
      }
      showing.current = true;
      setShow({ ...d, key: Date.now() });
      setTotal(Math.max(0, (Number(d.coins) || 0) - d.amount));
    };
    window.addEventListener("sbl:coins-earned", on);
    return () => window.removeEventListener("sbl:coins-earned", on);
  }, [isHost]);

  // fly coins into the piggy, then count the total up and close
  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    const flown = [];
    const start = () => {
      const piggy = piggyRef.current?.getBoundingClientRect();
      const n = Math.max(3, Math.min(12, show.amount));
      const tx = piggy ? piggy.left + piggy.width / 2 : window.innerWidth / 2;
      const ty = piggy ? piggy.top + piggy.height * 0.3 : window.innerHeight / 2;
      const sx = window.innerWidth / 2;
      const sy = Math.max(40, (piggy ? piggy.top : window.innerHeight / 2) - 170);
      let landed = 0;
      for (let i = 0; i < n; i += 1) {
        const el = document.createElement("span");
        el.className = "sbl-coin-fly sbl-coin-fly-big";
        el.innerHTML = '<span class="sbl-coin-face" style="width:42px;height:42px;font-size:22px"><span>S</span></span>';
        el.style.left = `${sx - 21 + (i - n / 2) * 18}px`;
        el.style.top = `${sy - 21}px`;
        document.body.appendChild(el);
        flown.push(el);
        if (reduced()) continue;
        const anim = el.animate(
          [
            { transform: "translate(0,-30px) scale(0.5)", opacity: 0 },
            { transform: "translate(0,0) scale(1.15)", opacity: 1, offset: 0.25 },
            { transform: `translate(${tx - sx - (i - n / 2) * 18}px,${ty - sy}px) scale(0.55)`, opacity: 1 },
          ],
          { duration: 800, delay: 120 + i * 90, easing: "cubic-bezier(.5,.05,.4,1)", fill: "forwards" }
        );
        anim.onfinish = () => {
          el.remove();
          landed += 1;
          if (cancelled) return;
          setWiggle((w) => w + 1);
          setTotal((t) => t + Math.ceil(show.amount / n));
          if (landed === n) setTotal(Number(show.coins) || 0);
        };
      }
      if (reduced()) {
        flown.forEach((el) => el.remove());
        setTotal(Number(show.coins) || 0);
      }
    };
    const t0 = setTimeout(start, 250);
    timer.current = setTimeout(() => closeRef.current(), 3200 + Math.min(12, show.amount) * 90);
    return () => {
      cancelled = true;
      clearTimeout(t0);
      flown.forEach((el) => el.remove());
    };
  }, [show]);

  if (!isHost) return null;
  if (teacherMsgs.list.length) return <TeacherMessages list={teacherMsgs.list} onDone={teacherMsgs.acknowledge} />;
  if (!show) return null;
  const bank = show.bank || BANK_REASONS.has(show.reason);
  const why =
    show.reason === "gift" && show.note
      ? `from your teacher — “${show.note}”`
      : WHY[show.reason];
  return (
    <ModalPortal>
      <div className="sbl-cc-pop" onClick={() => closeRef.current()} role="status" aria-live="polite">
        <div className="sbl-cc-card" key={show.key}>
          <h2>
            {show.reason === "class-bank" ? "You added" : "You earned"} <span>{show.amount}</span> coin{show.amount === 1 ? "" : "s"}
            {bank ? " for your class" : ""}!
          </h2>
          {why && <p className="sbl-cc-why">{why}</p>}
          <span ref={piggyRef} className="sbl-cc-piggy" key={wiggle}>
            <Piggy wiggle={wiggle > 0} />
          </span>
          <p className="sbl-cc-total">
            {bank ? "Class coin bank" : "Your bank"}: <b>{Math.min(total, Number(show.coins) || total).toLocaleString()}</b> coins
          </p>
          <small>Tap to close</small>
        </div>
      </div>
    </ModalPortal>
  );
};

export default CoinCelebration;
